import { ArrowUpRight, Instagram, Mail, Phone } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { SEO, SITE } from "@/config/site";
import { Fade, Lines, Reveal } from "@/components/motion";
import { BookingButton, WhatsAppAssist, WhatsAppIcon } from "@/components/CTA";
import StudioMap from "@/components/StudioMap";

const CONTACT_ROWS = [
  { icon: Phone, label: "Telefono", value: SITE.contacts.phone, href: SITE.contacts.phoneHref, testId: "contact-phone" },
  { icon: Mail, label: "Email", value: SITE.contacts.email, href: SITE.contacts.emailHref, testId: "contact-email" },
  { icon: null, label: "WhatsApp", value: SITE.contacts.whatsapp, href: SITE.contacts.whatsappHref, testId: "contact-whatsapp" },
  { icon: Instagram, label: "Instagram", value: SITE.contacts.instagram, href: SITE.contacts.instagramHref, testId: "contact-instagram" },
];

export default function Contatti() {
  useSEO(SEO.contatti);
  return (
    <>
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-32 sm:pt-44 pb-20 sm:pb-28">
        <Fade>
          <p className="eyebrow text-leaf-deep">Contatti · Bologna</p>
        </Fade>
        <h1 className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
          <Lines delay={0.2} lines={["Prenota", <em key="e" className="text-leaf-deep">una visita</em>]} />
        </h1>
        <Fade delay={0.6}>
          <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/65">
            Puoi prenotare direttamente online scegliendo il giorno e l'orario disponibili.
          </p>
          <div className="mt-10">
            <BookingButton label="Prenota online" testId="contact-booking-cta" className="px-10 py-5" />
            <p className="mt-5 text-xs text-ink/45">
              La prenotazione avviene tramite MioDottore, in autonomia e in pochi minuti.
            </p>
          </div>
        </Fade>
      </section>

      <section className="border-t border-ink/10 bg-mist" aria-label="Assistenza alla prenotazione">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 py-20 sm:py-24 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-balance">
              Preferisci essere guidato passo passo?
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink/60">
              Se la prenotazione online non ti è comoda, scrivimi su WhatsApp. Se possibile,
              indicami già la sede preferita (Bologna o Casalecchio di Reno) e orientativamente i
              giorni e le fasce orarie in cui sei disponibile, così potrò aiutarti più rapidamente a
              fissare l'appuntamento.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <WhatsAppAssist testId="contact-whatsapp-assist" />
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28" aria-label="Recapiti e studio">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-24">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Recapiti</h2>
            <ul className="mt-9 flex flex-col">
              {CONTACT_ROWS.map(({ icon: Icon, label, value, href, testId }) => (
                <li
                  key={label}
                  data-testid={testId}
                  className="flex items-center gap-5 border-t border-ink/10 py-5 last:border-b"
                >
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-ink/10 text-leaf-deep">
                    {Icon ? (
                      <Icon className="h-4 w-4" strokeWidth={1.75} />
                    ) : (
                      <WhatsAppIcon className="h-4 w-4" />
                    )}
                  </span>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/45">
                      {label}
                    </p>
                    <a
                      href={href}
                      {...(href.startsWith("http")
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      className="mt-1 inline-block text-sm text-ink/80 transition-colors duration-200 hover:text-leaf-deep"
                    >
                      {value}
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Sedi</h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-ink/55">
              Ricevo in tre studi: uno a Bologna e due a Casalecchio di Reno.
            </p>
            <div className="mt-8 flex flex-col">
              {SITE.locations.map((loc, i) => (
                <div
                  key={loc.query}
                  data-testid={`location-${i + 1}`}
                  className="border-t border-ink/10 py-6 last:border-b"
                >
                  <p className="eyebrow text-wave-deep">{loc.city}</p>
                  <p className="mt-2.5 font-display text-lg sm:text-xl tracking-tight text-ink/85">
                    {loc.lines.join(" — ")}
                  </p>
                  <a
                    href={loc.directions}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`location-directions-${i + 1}`}
                    className="group mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-ink transition-colors duration-200 hover:text-leaf-deep"
                  >
                    Indicazioni
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-20 sm:pb-28" aria-label="Mappa delle sedi">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Dove ricevo</h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-ink/55">
            Una sede a Bologna e due sedi a Casalecchio di Reno, tutte sulla stessa mappa.
          </p>
          <div className="mt-9">
            <StudioMap />
          </div>
        </Reveal>
      </section>
    </>
  );
}
